﻿// packages/shared/src/types/index.ts

// ===== TYPE DEFINITIONS =====

export interface LinkedAccount {
  providerId: string;
  displayName?: string;
  email?: string;
  photoURL?: string;
  linkedAt: Date;
}

// New rating system types
export interface PlayerRatings {
  utr: number | null; // UTR rating (1.0 - 16.5)
  ntrp: number | null; // NTRP rating (1.0 - 7.0)
}

export interface EmailPreferences {
  challengeReceived?: boolean;
  challengeAccepted?: boolean;
  challengeRejected?: boolean;
  accountSecurity?: boolean;
}

export interface User {
  uid: string;
  email: string;
  name: string;
  profilePicture?: string;
  preferences: {
    level: string; // Keep for backward compatibility, but will show 'Not Rated'
    ratings: PlayerRatings; // New rating system
    preferredCourts: string[];
    defaultDuration: number;
    timezone: string;
    emailNotifications?: boolean; 
    emailPreferences?: EmailPreferences;
  };
  
  linkedAccounts?: LinkedAccount[];
  createdAt: Date;
  updatedAt: Date;
  emailVerified: boolean;
}

export interface HeatmapTreeDocument {
  id: string; // Format: "YYYY-WW" (UTC week, e.g., "2025-03")
  weekStart: Date; // Monday 00:00:00 UTC
  weekEnd: Date; // Sunday 23:59:59 UTC
  
  segments: {
    // All stored in UTC time slots
    level0: number[]; // 2016 slots (7 days × 24 hours × 12 five-minute slots)
    level1: number[]; // 168 slots (7 days × 24 hours)  
    level2: number[]; // 7 slots (7 days)
    level3: number; // Week total
  };
  
  metadata: {
    totalSlots: number;
    courtCounts: { [courtId: string]: number };
    levelCounts: { [level: string]: number };
    cityCounts: { [city: string]: number };
    countryCounts: { [country: string]: number };
  };
  
  lastUpdated: Date;
  version: number;
}

export interface TimezoneWeekBounds {
  userWeekStart: Date;
  userWeekEnd: Date;
  utcWeekStart: Date;
  utcWeekEnd: Date;
}

export interface HeatmapGenerationOptions {
  usePrecomputedTrees?: boolean;
  fallbackToRawData?: boolean;
  maxCacheAge?: number; // in milliseconds
}

export interface Chat {
  id: string;
  challengeId: string;
  participants: [string, string]; // [userId1, userId2]
  participantNames: [string, string]; // [userName1, userName2]
  matchTime: Date;
  courtName: string;
  createdAt: Date; // When challenge was accepted
  expiresAt: Date; // matchTime + 48 hours
  lastMessageAt?: Date;
  messageCount: number;
}

export interface Message {
  id: string;
  chatId: string;
  senderId: string;
  senderName: string;
  content: string;
  createdAt: Date;
}
export interface Court {
  id: string;
  name: string;
  location: {
    city: string;
    address: string;
    country?: string;
    coordinates: [number, number]; // [longitude, latitude]
  };
  amenities: string[];
  pricing: {
    hourly: number;
    currency: string;
  };
  availability: {
    timezone: string;
    schedule: Record<string, any>;
  };
  // All fields from CSV data
  details: {
    url?: string;
    telephone?: string;
    website?: string;
    google_maps_url?: string;
    facility_type?: string;
    matches_played?: number;
    total_courts?: number;
    indoor_courts?: number;
    court_fees?: string;
    court_type?: string;
    lights?: boolean;
    region?: string;
    postal_code?: string;
    state?: string; // Optional: for countries with states/provinces (e.g., US states, Canadian provinces)
    // New fields from CSV
    indoor_courts_description?: string; // e.g., "4 all year", "6 all year"
    court_surface?: string; // Renamed from court_type for clarity
    amenities_raw?: string; // Original amenities string from CSV
  };
  managerId?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Court service interfaces
export interface CourtFilters {
  search?: string;
  city?: string;
  country?: string;
  facilityType?: string;
  courtSurface?: string;
  lights?: boolean;
  hasIndoorCourts?: boolean;
  courtFees?: string;
  sortBy?: 'name' | 'price' | 'total_courts';
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedCourtsResult {
  courts: Court[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  totalCount?: number;
  nextPageToken?: string;
  prevPageToken?: string;
}

export interface Booking {
  id: string;
  userId: string;
  courtId: string;
  startTime: Date;
  endTime: Date;
  duration: number; // in minutes
  level: string; // Keep for backward compatibility
  ratings: PlayerRatings; // New rating system
  status: 'pending' | 'active' | 'cancelled' | 'completed';
  players: string[];
  maxPlayers: number;
  text: string;
  location: {
    city: string;
    country?: string;
    coordinates: [number, number]; // [longitude, latitude]
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface Filters {
  level: string; // Keep for backward compatibility
  utrMin: number | null; // UTR range filter
  utrMax: number | null;
  ntrpMin: number | null; // NTRP range filter
  ntrpMax: number | null;
  court: string;
  duration: string | number;
  city: string;
  timezone: string;
  country?: string;
  radius?: number; // Add radius filter (in miles)
}

export interface TimeRange {
  startDate: Date;
  endDate: Date;
  timezone?: string; // Optional timezone information
}

export interface PlayerSlot {
  id: string;
  playerId: string;
  playerName: string;
  playerLevel: string; // Keep for backward compatibility
  playerRatings: PlayerRatings; // New rating system
  court: string;
  courtId: string;
  startTime: Date;
  duration: number;
  city: string;
  country?: string;
  state?: string; // Optional: for countries with states/provinces (e.g., US states)
  visibility: 'public' | 'private';
  timezone: string;
  booked?: boolean;
  bookedAt?: Date;
  bookedBy?: string;
  challengeId?: string;
  location?: {
    coordinates: [number, number]; // [longitude, latitude]
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface Challenge {
  id: string;
  fromUserId: string;
  fromUserName: string;
  fromPlayerRatings: PlayerRatings;
  toUserId: string;
  toUserName: string;
  toPlayerRatings: PlayerRatings;
  slotId: string;
  startTime: Date;
  endTime: Date;
  courtId: string;
  courtName: string;

  message: string;
  status: 'pending' | 'accepted' | 'rejected' | 'withdrawn' | 'expired';
  createdAt: Date;
  updatedAt: Date;

  cancelledBy?: string;
  cancelledAt?: Date;
  cancellationMessage?: string;
}

export interface HeatmapData {
  [key: number]: number[];
}
// Rating utility functions
export const UTR_MIN = 1.0;
export const UTR_MAX = 16.5;
export const NTRP_MIN = 1.0;
export const NTRP_MAX = 7.0;

export const formatUTRRating = (rating: number | null): string => {
  if (rating === null || rating === 0) return 'Not Rated';
  return rating.toFixed(1);
};

export const formatNTRPRating = (rating: number | null): string => {
  if (rating === null || rating === 0) return 'Not Rated';
  return rating.toFixed(1);
};

export const formatPlayerRatings = (ratings: PlayerRatings): string => {
  const utr = formatUTRRating(ratings.utr);
  const ntrp = formatNTRPRating(ratings.ntrp);
  
  if (utr === 'Not Rated' && ntrp === 'Not Rated') {
    return 'Not Rated';
  }
  
  const parts = [];
  if (utr !== 'Not Rated') parts.push(`UTR: ${utr}`);
  if (ntrp !== 'Not Rated') parts.push(`NTRP: ${ntrp}`);
  
  return parts.join(' • ');
};

export interface Challenge {
  id: string;
  fromUserId: string;
  fromUserName: string;
  toUserId: string;
  toUserName: string;
  slotId: string;
  startTime: Date;
  endTime: Date;
  courtId: string;
  courtName: string;
  message: string;
  status: 'pending' | 'accepted' | 'rejected' | 'withdrawn' | 'expired';
  createdAt: Date;
  updatedAt: Date;
}

export interface CourtReport {
  id: string;
  courtId: string;
  courtName: string;
  reportedBy: string;
  reporterName: string;
  reporterEmail: string;
  message: string;
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  createdAt: Date;
  updatedAt: Date;
  adminNotes?: string;
  resolvedBy?: string;  
  resolvedAt?: Date;
  // New fields for court addition requests
  requestType?: 'edit' | 'new_court';
  courtAddress?: string; // For new court requests  
  additionalInfo?: string; // For new court requests
}